import type { ColumnDef, RowSelectionState } from "@tanstack/react-table";
import { ChevronLeft, ChevronRight, Search, X } from "lucide-react";
import { DataTable } from "@/components/data-table";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/components/ui/input-group";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

export interface IndexTab {
  value: string;
  label: string;
  count?: number;
}

/**
 * Übersichtsseiten-Muster: eine Karte mit Filter-Tabs, Such-/Filterleiste,
 * Bulk-Leiste bei Auswahl, dichter Tabelle und Pagination.
 */
export function IndexTable<TData>({
  tabs,
  tab,
  onTabChange,
  search,
  filters,
  columns,
  data,
  getRowId,
  onRowClick,
  rowLabel,
  loading,
  empty,
  selection,
  onSelectionChange,
  bulkActions,
  pagination,
}: {
  tabs?: IndexTab[];
  tab?: string;
  onTabChange?: (value: string) => void;
  search?: {
    value: string;
    onChange: (v: string) => void;
    placeholder: string;
    label: string;
  };
  filters?: React.ReactNode;
  columns: ColumnDef<TData, unknown>[];
  data: TData[];
  getRowId?: (row: TData) => string;
  onRowClick?: (row: TData) => void;
  rowLabel?: (row: TData) => string;
  loading?: boolean;
  empty?: React.ReactNode;
  selection?: RowSelectionState;
  onSelectionChange?: (next: RowSelectionState) => void;
  /** Wird statt der Suchleiste gezeigt, solange Zeilen ausgewählt sind. */
  bulkActions?: (selectedIds: string[], clear: () => void) => React.ReactNode;
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
    onPageChange: (page: number) => void;
    disabled?: boolean;
  };
}) {
  const selectedIds = selection
    ? Object.keys(selection).filter((k) => selection[k])
    : [];
  const clear = () => onSelectionChange?.({});
  const pageCount = pagination
    ? Math.max(1, Math.ceil(pagination.total / pagination.pageSize))
    : 1;

  return (
    <Card className="gap-0 py-0">
      {tabs && tabs.length > 0 && (
        <Tabs value={tab} onValueChange={(v) => onTabChange?.(v)}>
          <div className="overflow-x-auto border-b px-2 py-1.5">
            <TabsList className="h-auto gap-0.5 bg-transparent p-0">
              {tabs.map((t) => (
                <TabsTrigger
                  key={t.value}
                  value={t.value}
                  className="h-7 flex-none rounded-lg px-2.5 font-medium text-muted-foreground hover:bg-accent data-active:bg-[#e3e3e3] data-active:text-foreground data-active:shadow-none"
                >
                  {t.label}
                  {t.count !== undefined && (
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {t.count}
                    </span>
                  )}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
        </Tabs>
      )}

      {(search || filters || selectedIds.length > 0) && (
        <div className="flex min-h-12 flex-wrap items-center gap-2 border-b px-3 py-2">
          {selectedIds.length > 0 && bulkActions ? (
            <>
              <span className="font-medium">
                {selectedIds.length} ausgewählt
              </span>
              <div className="flex flex-wrap items-center gap-1.5">
                {bulkActions(selectedIds, clear)}
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto"
                onClick={clear}
              >
                Auswahl aufheben
              </Button>
            </>
          ) : (
            <>
              {search && (
                <InputGroup className="h-8 max-w-sm min-w-48 flex-1 bg-card">
                  <InputGroupAddon>
                    <Search />
                  </InputGroupAddon>
                  <InputGroupInput
                    value={search.value}
                    onChange={(e) => search.onChange(e.target.value)}
                    placeholder={search.placeholder}
                    aria-label={search.label}
                  />
                  {search.value && (
                    <InputGroupAddon align="inline-end">
                      <InputGroupButton
                        size="icon-xs"
                        onClick={() => search.onChange("")}
                        aria-label="Suche leeren"
                      >
                        <X />
                      </InputGroupButton>
                    </InputGroupAddon>
                  )}
                </InputGroup>
              )}
              {filters && (
                <div className="flex flex-wrap items-center gap-2">
                  {filters}
                </div>
              )}
            </>
          )}
        </div>
      )}

      <DataTable
        columns={columns}
        data={data}
        getRowId={getRowId}
        onRowClick={onRowClick}
        rowLabel={rowLabel}
        loading={loading}
        empty={empty}
        selection={selection}
        onSelectionChange={onSelectionChange}
      />

      {pagination && pagination.total > 0 && (
        <div className="flex items-center justify-between gap-2 border-t bg-muted px-3 py-2">
          <span className="text-muted-foreground tabular-nums">
            {pagination.page * pagination.pageSize + 1}–
            {Math.min(
              pagination.total,
              (pagination.page + 1) * pagination.pageSize,
            )}{" "}
            von {pagination.total}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="secondary"
              size="icon-sm"
              onClick={() => pagination.onPageChange(pagination.page - 1)}
              disabled={pagination.page === 0 || pagination.disabled}
              aria-label="Vorherige Seite"
            >
              <ChevronLeft />
            </Button>
            <Button
              variant="secondary"
              size="icon-sm"
              onClick={() => pagination.onPageChange(pagination.page + 1)}
              disabled={pagination.page >= pageCount - 1 || pagination.disabled}
              aria-label="Nächste Seite"
            >
              <ChevronRight />
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
